import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { BookOpen, ExternalLink, Pencil, Trash2, Plus, GraduationCap, Award, PlayCircle } from 'lucide-react';
import { academyApi, POSTE_LABELS, LEVEL_LABELS } from './api';
import AcademyInternalPlayer from './AcademyInternalPlayer';

const EMPTY_FORM = {
  title: '',
  description: '',
  category: '',
  organism: '',
  duration: '',
  level: 'debutant',
  external_url: '',
  mandatory: false,
  postes: [],
  validity_months: 0,
  date_limite: '',
  points_mandatory: 10,
  points_optional: 5,
  is_active: true,
};

const AcademyCatalog = ({ isAdmin, onStartTraining, refreshKey }) => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterPoste, setFilterPoste] = useState('all');
  const [filterMandatory, setFilterMandatory] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [internalPlayerFor, setInternalPlayerFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin) {
        params.include_inactive = true;
        params.only_active = false;
      }
      if (filterPoste && filterPoste !== 'all') params.poste = filterPoste;
      if (filterMandatory) params.only_mandatory = true;
      const data = await academyApi.listTrainings(params);
      setTrainings(data || []);
    } catch (e) {
      toast.error("Erreur lors du chargement du catalogue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterPoste, filterMandatory, isAdmin, refreshKey]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title || '',
      description: t.description || '',
      category: t.category || '',
      organism: t.organism || '',
      duration: t.duration || '',
      level: t.level || 'debutant',
      external_url: t.external_url || '',
      mandatory: !!t.mandatory,
      postes: t.postes || [],
      validity_months: t.validity_months || 0,
      date_limite: t.date_limite || '',
      points_mandatory: t.points_mandatory || 10,
      points_optional: t.points_optional || 5,
      is_active: t.is_active !== false,
    });
    setDialogOpen(true);
  };

  const togglePoste = (p) => {
    setForm((f) => ({
      ...f,
      postes: f.postes.includes(p) ? f.postes.filter((x) => x !== p) : [...f.postes, p],
    }));
  };

  const save = async () => {
    if ((form.title || '').trim().length < 3) {
      toast.error("Le titre doit contenir au moins 3 caractères.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, validity_months: Number(form.validity_months) || 0 };
      if (editing) {
        await academyApi.updateTraining(editing.id, payload);
        toast.success("Formation mise à jour.");
      } else {
        await academyApi.createTraining(payload);
        toast.success("Formation créée.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer "${t.title}" ?`)) return;
    try {
      const res = await academyApi.deleteTraining(t.id);
      toast.success(res.soft_deleted ? "Formation archivée (des inscriptions existent)." : "Formation supprimée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression.");
    }
  };

  const start = (t) => {
    const isInternal = (t.organism || '').toLowerCase() === 'sada academy';
    if (isInternal) {
      // Module interne SADA → ouvrir le lecteur (leçons + quiz + certificat)
      setInternalPlayerFor(t);
      return;
    }
    // Formation externe : ouvrir le lien IMMÉDIATEMENT + inscription en background
    if (t.external_url) {
      window.open(t.external_url, '_blank', 'noopener,noreferrer');
    }
    academyApi.startEnrollment(t.id)
      .then((enrollment) => {
        toast.success("Inscription enregistrée. Bonne formation !");
        if (onStartTraining) onStartTraining(enrollment);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Erreur lors du démarrage.");
      });
  };

  const filtered = useMemo(() => trainings, [trainings]);

  return (
    <div className="space-y-4" data-testid="academy-catalog">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterPoste} onValueChange={setFilterPoste}>
            <SelectTrigger className="w-40" data-testid="academy-filter-poste">
              <SelectValue placeholder="Poste" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les postes</SelectItem>
              {Object.entries(POSTE_LABELS).map(([id, label]) => (
                <SelectItem key={id} value={id}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={filterMandatory} onCheckedChange={setFilterMandatory} data-testid="academy-filter-mandatory" />
            Obligatoires uniquement
          </label>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="academy-create-training-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle formation
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-8">Chargement…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          <GraduationCap className="w-10 h-10 mx-auto mb-2 opacity-40" />
          Aucune formation dans le catalogue pour l&apos;instant.
          {isAdmin && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-slate-600">
                Importez le catalogue par défaut (54 formations HP LIFE, Microsoft Learn, HubSpot, YALI + 6 modules internes SADA complets).
              </p>
              <Button
                onClick={async () => {
                  try {
                    const res = await academyApi.seedDefaultCatalog(false);
                    toast.success(`Catalogue importé : ${res.inserted.trainings} formations, ${res.inserted.lessons} leçons, ${res.inserted.questions} questions.`);
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Import impossible.");
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="academy-seed-default-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Importer le catalogue par défaut
              </Button>
            </div>
          )}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((t) => (
            <Card key={t.id} className={`border ${t.is_active ? '' : 'opacity-60'}`} data-testid={`academy-training-card-${t.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 leading-tight">{t.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{t.organism || '—'} · {t.duration || 'Durée non précisée'}</p>
                  </div>
                  {t.mandatory ? (
                    <Badge className="bg-red-100 text-red-700 border-red-200 shrink-0">Obligatoire</Badge>
                  ) : (
                    <Badge className="bg-sky-100 text-sky-700 border-sky-200 shrink-0">Optionnelle</Badge>
                  )}
                </div>
                {t.description && (
                  <p className="text-sm text-slate-600 line-clamp-3">{t.description}</p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {(t.postes || []).length === 0 ? (
                    <Badge variant="outline" className="text-xs">Tous postes</Badge>
                  ) : (
                    (t.postes || []).map((p) => (
                      <Badge key={p} variant="outline" className="text-xs">{POSTE_LABELS[p] || p}</Badge>
                    ))
                  )}
                  <Badge variant="outline" className="text-xs">{LEVEL_LABELS[t.level] || t.level}</Badge>
                  {t.category && <Badge variant="outline" className="text-xs">{t.category}</Badge>}
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t">
                  <div className="flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" />
                    {t.mandatory ? `${t.points_mandatory} pts` : `${t.points_optional} pts`}
                  </div>
                  <div>{t.validity_months ? `Validité : ${t.validity_months} mois` : 'Validité permanente'}</div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {t.external_url ? (
                    <Button
                      size="sm"
                      onClick={() => start(t)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`academy-start-training-${t.id}`}
                    >
                      <BookOpen className="w-4 h-4 mr-1" />
                      Commencer la formation
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  ) : (t.organism || '').toLowerCase() === 'sada academy' ? (
                    <Button
                      size="sm"
                      onClick={() => start(t)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`academy-open-internal-${t.id}`}
                    >
                      <PlayCircle className="w-4 h-4 mr-1" />
                      Ouvrir le module SADA
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled title="Aucun lien externe défini">
                      Lien non défini
                    </Button>
                  )}
                  {isAdmin && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`academy-edit-training-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(t)} data-testid={`academy-delete-training-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="academy-training-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier la formation' : 'Nouvelle formation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="academy-form-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Catégorie</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex: Bureautique, Vente…" />
              </div>
              <div>
                <Label>Organisme</Label>
                <Input value={form.organism} onChange={(e) => setForm({ ...form, organism: e.target.value })} placeholder="Ex: HP LIFE, HPCI…" />
              </div>
              <div>
                <Label>Durée estimée</Label>
                <Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Ex: 2h, 1 semaine…" />
              </div>
              <div>
                <Label>Niveau</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(LEVEL_LABELS).map(([id, label]) => (
                      <SelectItem key={id} value={id}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Lien externe (URL de la formation)</Label>
              <Input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" data-testid="academy-form-url" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.mandatory} onCheckedChange={(v) => setForm({ ...form, mandatory: v })} data-testid="academy-form-mandatory" />
              <Label>Formation obligatoire</Label>
            </div>
            <div>
              <Label>Postes concernés (laisser vide = tous)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.entries(POSTE_LABELS).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => togglePoste(id)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      form.postes.includes(id)
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400'
                    }`}
                    data-testid={`academy-form-poste-${id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Validité (mois, 0=permanente)</Label>
                <Input type="number" min="0" value={form.validity_months} onChange={(e) => setForm({ ...form, validity_months: e.target.value })} />
              </div>
              <div>
                <Label>Points obligatoire</Label>
                <Input type="number" min="0" value={form.points_mandatory} onChange={(e) => setForm({ ...form, points_mandatory: e.target.value })} />
              </div>
              <div>
                <Label>Points optionnelle</Label>
                <Input type="number" min="0" value={form.points_optional} onChange={(e) => setForm({ ...form, points_optional: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Date limite d&apos;échéance globale (optionnel)</Label>
              <Input type="date" value={form.date_limite || ''} onChange={(e) => setForm({ ...form, date_limite: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Active (visible dans le catalogue)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="academy-form-save">
              {saving ? 'Enregistrement…' : editing ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {internalPlayerFor && (
        <AcademyInternalPlayer
          trainingId={internalPlayerFor.id}
          isAdmin={isAdmin}
          onClose={() => {
            setInternalPlayerFor(null);
            if (onStartTraining) onStartTraining();
          }}
        />
      )}
    </div>
  );
};

export default AcademyCatalog;
